import React, { useEffect, useState, useContext, useImperativeHandle, forwardRef, useRef } from 'react';
import SnackbarAlertContext from '../../../context/SnackbarAlertContext';
import CloseIcon from '@mui/icons-material/Close';
import './RightSidebar.css';
import ReactMarkdown from 'react-markdown';
import { trackEvent } from '../../../utils/eventTracking';
import { UserProfileContext } from '../../../UserProfileContext';
import ChatInput from './ChatInput';
import { chatAgentProxyFetch, transcribeAudio } from '../../../api_helper/api';

/**
 * Spinner
 * Simple animated loading indicator for async operations in chat.
 */
const Spinner = () => (
	<div className="spinner">
		<div className="bounce1" />
		<div className="bounce2" />
		<div className="bounce3" />
	</div>
);

/**
 * RightSidebar
 * Chat sidebar component for interacting with the SportWise Agent.
 * Handles chat UI, conversation state, and LangGraph API integration.
 *
 * Props:
 *   showModal (bool): Whether the sidebar is open.
 *   setShowModal (func): Function to toggle sidebar visibility.
 *   dataspaceid (string): Current dataspace context.
 *   jwt (string): User authentication token.
 *
 * Exposes:
 *   resetAndDeleteAssistant: Resets state and deletes agent on parent or tab close.
 */
const RightSidebar = forwardRef(function RightSidebar(props, ref) {
	const { showModal, setShowModal, dataspaceid, jwt, dataSpaceDataReady = true } = props;

	const userProfile = useContext(UserProfileContext)[0];
	const snackbarAlertContext = useContext(SnackbarAlertContext);

	// User's current input in the chat box
	const [postContent, setPostContent] = useState('');
	// Array of all messages (user & agent) in this conversation
	const [conversation, setConversation] = useState([]);
	// LangGraph assistant instance for this session
	const [assistant_id, setAssistant_id] = useState('');
	// Thread ID for maintaining conversation context
	const [thread_id, setThread_id] = useState('');
	// idle | initializing | ready | failed
	const [coachStatus, setCoachStatus] = useState('idle');
	const messagesEndRef = useRef(null);
	// Track previous dataspaceid to detect changes
	const prevDataspaceIdRef = useRef(dataspaceid);
	const assistantIdRef = useRef('');
	const threadIdRef = useRef('');

	const COACH_INIT_ERROR =
		"Couldn't load DataSpace data for Coach. Refresh the page and try again.";
	const COACH_RESPONSE_ERROR =
		'SportWise Coach could not complete that request. Please try again.';

	const parseErrorBody = async (response) => {
		try {
			const contentType = response.headers.get('content-type') || '';
			if (contentType.includes('application/json')) {
				const json = await response.json();
				return json?.message || json?.error || json?.detail || JSON.stringify(json);
			}
			const text = await response.text();
			return text?.slice(0, 300) || response.statusText;
		} catch {
			return response.statusText || 'Unknown error';
		}
	};

	const extractStreamError = (part) => {
		if (!part?.trim()) return null;
		const lines = part.split(/\r?\n/);
		let eventType = '';
		let eventData = '';
		for (const line of lines) {
			if (line.startsWith('event: ')) {
				eventType = line.substring(7).trim();
			} else if (line.startsWith('data: ')) {
				eventData += line.substring(6);
			}
		}
		if (!eventData.trim()) return null;

		try {
			const parsed = JSON.parse(eventData);
			if (eventType === 'error' || parsed?.error || parsed?.status === 'error') {
				return (
					parsed?.message ||
					parsed?.error ||
					(typeof parsed === 'string' ? parsed : 'Coach stream error')
				);
			}
		} catch {
			if (eventType === 'error') {
				return eventData.trim();
			}
		}
		return null;
	};

	// Scroll to bottom when conversation updates
	useEffect(() => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
		}
	}, [conversation]);

	// Audio Recording State
	const [isRecording, setIsRecording] = useState(false);
	const processorRef = useRef(null);
	const sourceRef = useRef(null);
	const audioChunksRef = useRef([]);
	const audioContextRef = useRef(null);
	const [audioAnalyser, setAudioAnalyser] = useState(null);
	const isRecordingCancelledRef = useRef(false);
	const [isTranscribing, setIsTranscribing] = useState(false);

	// Silence detection refs
	const speechActiveRef = useRef(false);
	const silenceStartTimeRef = useRef(null);
	const streamRef = useRef(null);
	// Constants for VAD
	const SILENCE_THRESHOLD = 0.02; // Amplitude threshold
	const SILENCE_DELAY = 1000; // ms to keep recording after silence detected
	const MIN_CLIPPED_SILENCE = 200; // ms of silence to insert when we skip a silent block

	/**
	 * WAV Encoding Helpers
	 */
	const floatTo16BitPCM = (output, offset, input) => {
		for (let i = 0; i < input.length; i++, offset += 2) {
			const s = Math.max(-1, Math.min(1, input[i]));
			output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
		}
	};

	const writeString = (view, offset, string) => {
		for (let i = 0; i < string.length; i++) {
			view.setUint8(offset + i, string.charCodeAt(i));
		}
	};

	const encodeWAV = (samples, sampleRate) => {
		const buffer = new ArrayBuffer(44 + samples.length * 2);
		const view = new DataView(buffer);

		/* RIFF identifier */
		writeString(view, 0, 'RIFF');
		/* file length */
		view.setUint32(4, 36 + samples.length * 2, true);
		/* RIFF type */
		writeString(view, 8, 'WAVE');
		/* format chunk identifier */
		writeString(view, 12, 'fmt ');
		/* format chunk length */
		view.setUint32(16, 16, true);
		/* sample format (raw) */
		view.setUint16(20, 1, true);
		/* channel count */
		view.setUint16(22, 1, true);
		/* sample rate */
		view.setUint32(24, sampleRate, true);
		/* byte rate (sample rate * block align) */
		view.setUint32(28, sampleRate * 2, true);
		/* block align (channel count * bytes per sample) */
		view.setUint16(32, 2, true);
		/* bits per sample */
		view.setUint16(34, 16, true);
		/* data chunk identifier */
		writeString(view, 36, 'data');
		/* data chunk length */
		view.setUint32(40, samples.length * 2, true);

		floatTo16BitPCM(view, 44, samples);

		return view;
	};

	const mergeBuffers = (audioData) => {
		let totalLength = 0;
		for (let i = 0; i < audioData.length; i++) {
			totalLength += audioData[i].length;
		}
		const result = new Float32Array(totalLength);
		let offset = 0;
		for (let i = 0; i < audioData.length; i++) {
			result.set(audioData[i], offset);
			offset += audioData[i].length;
		}
		return result;
	};

	/**
	 * Transcribe audio using Azure OpenAI Whisper via backend API
	 */
	const handleTranscribeAudio = async (audioBlob) => {
		try {
			const formData = new FormData();
			formData.append('file', audioBlob, 'audio.wav');

			const response = await transcribeAudio(formData);

			if (response.data && response.data.text) {
				trackEvent({
					userDetails: { userId: userProfile._id, email: userProfile.email },
					eventDetails: {
						types: ['KissMetrics', 'Segment', 'Encharge', 'AppInsights', 'GA4'],
						eventName: 'WebApp_User voice transcription success',
					},
				});
				// Append transcribed text to current input
				setPostContent((prev) => (prev ? `${prev} ${response.data.text}` : response.data.text));
			}
		} catch (error) {
			console.error('Error transcribing audio:', error);
			snackbarAlertContext.setSnackbarAlert({
				severity: 'error',
				msg: 'Failed to transcribe audio. Please try again.',
			});
		}
	};

	/**
	 * Stop Recording Handler
	 */
	const stopRecording = async () => {
		if (isRecordingCancelledRef.current) {
			console.log('Recording cancelled, skipping transcription');
			setIsRecording(false);
			setIsTranscribing(false);
			// Cleanup
			cleanupAudio();
			return;
		}

		// Process collected chunks
		if (audioChunksRef.current.length > 0) {
			const mergedSamples = mergeBuffers(audioChunksRef.current);
			// Use the context sample rate
			const sampleRate = audioContextRef.current ? audioContextRef.current.sampleRate : 44100;
			const wavData = encodeWAV(mergedSamples, sampleRate);
			const audioBlob = new Blob([wavData], { type: 'audio/wav' });

			// Cleanup before transcribing
			cleanupAudio();

			await handleTranscribeAudio(audioBlob);
		} else {
			cleanupAudio();
		}

		setIsRecording(false);
		setIsTranscribing(false);
	};

	const cleanupAudio = () => {
		if (processorRef.current) {
			processorRef.current.disconnect();
			processorRef.current = null;
		}
		if (sourceRef.current) {
			sourceRef.current.disconnect();
			sourceRef.current = null;
		}
		if (audioContextRef.current) {
			audioContextRef.current.close();
			audioContextRef.current = null;
		}
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => track.stop());
			streamRef.current = null;
		}
		setAudioAnalyser(null);
	};

	/**
	 * Toggle Recording
	 */
	const handleRecordClick = async () => {
		if (isRecording) {
			// STOP RECORDING
			setIsTranscribing(true);
			await stopRecording();
		} else {
			// START RECORDING
			try {
				trackEvent({
					userDetails: { userId: userProfile._id, email: userProfile.email },
					eventDetails: {
						types: ['KissMetrics', 'Segment', 'Encharge', 'AppInsights', 'GA4'],
						eventName: 'WebApp_User started voice recording',
					},
				});
				const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
				streamRef.current = stream;

				// Reset refs
				audioChunksRef.current = [];
				isRecordingCancelledRef.current = false;
				speechActiveRef.current = false;
				silenceStartTimeRef.current = null;

				// Setup Audio Context
				const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
				audioContextRef.current = audioCtx;

				const analyser = audioCtx.createAnalyser();
				analyser.fftSize = 256;
				setAudioAnalyser(analyser);

				const source = audioCtx.createMediaStreamSource(stream);
				sourceRef.current = source;

				// Create ScriptProcessor for silence detection and recording
				// Buffer size 4096 is ~92ms at 44.1kHz
				const processor = audioCtx.createScriptProcessor(4096, 1, 1);
				processorRef.current = processor;

				processor.onaudioprocess = (e) => {
					if (isRecordingCancelledRef.current) return;

					const inputData = e.inputBuffer.getChannelData(0);

					// Calculate RMS to detect silence
					let sum = 0;
					for (let i = 0; i < inputData.length; i++) {
						sum += inputData[i] * inputData[i];
					}
					const rms = Math.sqrt(sum / inputData.length);

					// Determine if this chunk is silence or speech
					if (rms > SILENCE_THRESHOLD) {
						// Speech detected
						speechActiveRef.current = true;
						silenceStartTimeRef.current = null;
						// Clone buffer to save it
						audioChunksRef.current.push(new Float32Array(inputData));
					} else {
						// Silence detected
						if (speechActiveRef.current) {
							// We were speaking recently
							if (silenceStartTimeRef.current === null) {
								silenceStartTimeRef.current = Date.now();
							}

							const timeSinceSilence = Date.now() - silenceStartTimeRef.current;

							if (timeSinceSilence < SILENCE_DELAY) {
								// Keep recording trailing silence for context
								audioChunksRef.current.push(new Float32Array(inputData));
							} else {
								// Silence has been too long, stop recording this segment
								speechActiveRef.current = false;
								silenceStartTimeRef.current = null;

								// Optional: Insert a very small silence marker if we want to separate segments
								// but for now we just skip pushing chunks (trimming silence)
							}
						}
						// If speechActive is false and we are silent, we ignore the data (trim leading/interim silence)
					}
				};

				// Connect graph
				source.connect(analyser);
				analyser.connect(processor);
				processor.connect(audioCtx.destination); // Needed for Chrome to fire events

				setIsRecording(true);
			} catch (err) {
				console.error('Error accessing microphone:', err);
				snackbarAlertContext.setSnackbarAlert({
					severity: 'error',
					msg: 'Could not access microphone. Please check permissions.',
				});
			}
		}
	};

	const handleCancelRecording = async () => {
		if (isRecording) {
			trackEvent({
				userDetails: { userId: userProfile._id, email: userProfile.email },
				eventDetails: {
					types: ['KissMetrics', 'Segment', 'Encharge', 'AppInsights', 'GA4'],
					eventName: 'WebApp_User voice recording cancelled',
				},
			});
			isRecordingCancelledRef.current = true;
			cleanupAudio();
			setIsRecording(false);
		}
	};

	/**
	 * Handler for sending a new chat message.
	 * Ensures assistant and thread are created, then streams agent response.
	 * Updates conversation state with both user and agent messages.
	 * Handles Enter/Shift+Enter logic and disables input during streaming.
	 */

	/**
	 * Reset and delete assistant (exposed to parent and used for beforeunload)
	 * Deletes the agent instance from LangGraph and clears local state.
	 */
	const resetAndDeleteAssistant = async () => {
		const idToDelete = assistantIdRef.current || assistant_id;
		if (idToDelete) {
			await deleteAssistant(idToDelete);
		}
		assistantIdRef.current = '';
		threadIdRef.current = '';
		setAssistant_id('');
		setThread_id('');
		setCoachStatus('idle');
		setConversation([]);
		setPostContent(''); // Also clear chat input
	};

	// Expose resetAndDeleteAssistant and initializeAgent to parent via ref
	useImperativeHandle(ref, () => ({
		resetAndDeleteAssistant,
		initializeAgent,
	}));

	// Cleanup: Delete assistant when page/tab is closed
	useEffect(() => {
		const handleBeforeUnload = async (e) => {
			// Attempt synchronous reset (fetch may not complete)
			await resetAndDeleteAssistant();
		};
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [assistant_id]);

	const closeChatbotClickHandler = () => {
		setShowModal(false);
		trackEvent({
			userDetails: { userId: userProfile._id, email: userProfile.email },
			eventDetails: {
				types: ['KissMetrics', 'Segment', 'Encharge', 'AppInsights'],
				eventName: 'WebApp_User closed chatbot.',
			},
		});
	};

	/**
	 * deleteAssistant
	 * Makes API call to delete the assistant instance from LangGraph backend.
	 * Called when resetting or cleaning up the chat session.
	 */
	const deleteAssistant = async (id = assistant_id) => {
		if (!id) return;
		try {
			const response = await chatAgentProxyFetch({
				method: 'DELETE',
				jwt,
				body: {
					path: `assistants/${id}`,
				},
			});
			if (response.ok) {
				console.log('Assistant deleted successfully');
			} else {
				console.error('Error deleting assistant:', response.statusText);
			}
		} catch (error) {
			console.error('Error:', error);
		}
	};

	/**
	 * initializeAgent
	 * Creates assistant and thread for the LangChain agent when DataSpace is opened.
	 * This initialization happens proactively when the DataSpace loads, not on first message.
	 */
	const initializeAgent = async () => {
		try {
			if (!dataspaceid || !jwt) {
				console.log('⏸️ Skipping agent initialization: dataspaceid or jwt not available');
				return;
			}

			// If already initialized, skip
			if (assistantIdRef.current && threadIdRef.current && coachStatus === 'ready') {
				console.log('✅ Agent already initialized');
				return;
			}

			setCoachStatus('initializing');

			let newAssistantId;
			let newThreadId;

			// Get user's timezone
			const userTimezone = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone;

			// Create assistant (if not yet created)
			const response = await chatAgentProxyFetch({
				method: 'POST',
				jwt,
				body: {
					path: 'assistants',
					graph_id: 'agent',
					config: {
						configurable: {
							DataSpaceId: dataspaceid,
							jwt: jwt,
							timezone: userTimezone,
						},
					},
					metadata: {},
					if_exists: 'raise',
					name: 'sample',
				},
			});

			if (!response.ok) {
				const detail = await parseErrorBody(response);
				throw new Error(`Failed to create Coach assistant (${response.status}): ${detail}`);
			}

			const result = await response.json();
			newAssistantId = result.assistant_id;
			if (!newAssistantId) {
				throw new Error('Coach assistant was created without an assistant_id.');
			}
			console.log('✅ assistant created, newAssistantId:', newAssistantId);

			// Create thread if not yet created
			const threadResponse = await chatAgentProxyFetch({
				method: 'POST',
				jwt,
				body: {
					path: 'threads',
				},
			});

			if (!threadResponse.ok) {
				const detail = await parseErrorBody(threadResponse);
				throw new Error(`Failed to create Coach thread (${threadResponse.status}): ${detail}`);
			}

			const threadResult = await threadResponse.json();
			newThreadId = threadResult.thread_id;
			if (!newThreadId) {
				throw new Error('Coach thread was created without a thread_id.');
			}
			console.log('✅ thread created, newThreadId:', newThreadId);

			// Call for ensureDataLoaded to be run (by having no message in the request)
			const streamResponse = await chatAgentProxyFetch({
				method: 'POST',
				jwt,
				body: {
					path: `threads/${newThreadId}/runs/stream`,
					assistant_id: newAssistantId,
					thread_id: newThreadId,
					config: {
						configurable: {
							DataSpaceId: dataspaceid,
							jwt: jwt,
						},
					},
					input: {
						messages: [],
					},
					stream_mode: 'updates',
				},
			});

			if (!streamResponse.ok) {
				const detail = await parseErrorBody(streamResponse);
				throw new Error(`Coach data load failed (${streamResponse.status}): ${detail}`);
			}

			// Consume the stream to wait for completion and surface stream errors
			if (!streamResponse.body) {
				throw new Error('Coach initialization returned an empty response body.');
			}

			const reader = streamResponse.body.getReader();
			const decoder = new TextDecoder();
			let buffer = '';
			let streamError = null;

			while (true) {
				const { done, value } = await reader.read();
				if (done) break;
				buffer += decoder.decode(value, { stream: true });
				const parts = buffer.split(/\r?\n\r?\n/);
				buffer = parts.pop() || '';
				for (const part of parts) {
					const err = extractStreamError(part);
					if (err) streamError = err;
				}
			}
			if (buffer.trim()) {
				const err = extractStreamError(buffer);
				if (err) streamError = err;
			}

			if (streamError) {
				throw new Error(streamError);
			}

			console.log('✅ Agent initialization thread ran successfully');

			// Only mark ready after successful init stream
			assistantIdRef.current = newAssistantId;
			threadIdRef.current = newThreadId;
			setAssistant_id(newAssistantId);
			setThread_id(newThreadId);
			setCoachStatus('ready');
		} catch (error) {
			console.error('❌ Error initializing agent:', error);
			assistantIdRef.current = '';
			threadIdRef.current = '';
			setAssistant_id('');
			setThread_id('');
			setCoachStatus('failed');
			snackbarAlertContext.setSnackbarAlert({
				severity: 'error',
				msg: error?.message ? `${COACH_INIT_ERROR} ${error.message}` : COACH_INIT_ERROR,
			});
		}
	};

	// Initialize agent when DataSpace is opened AND grid data has finished loading
	useEffect(() => {
		const initializeAgentHandler = async () => {
			// Only cleanup if dataspaceid actually changed (not on initial mount)
			if (prevDataspaceIdRef.current && prevDataspaceIdRef.current !== dataspaceid) {
				await resetAndDeleteAssistant();
			}

			// Update the ref to current dataspaceid
			prevDataspaceIdRef.current = dataspaceid;

			// Wait until the parent has finished loading DataSpace rows/metadata.
			// New DataSpaces often show rows in the UI after this completes; starting Coach
			// earlier races GetDataSpaceRows and causes empty-table / init failures.
			if (!dataSpaceDataReady) {
				console.log('⏸️ Skipping agent initialization: DataSpace data not ready yet');
				return;
			}

			// Initialize agent for the new DataSpace
			if (dataspaceid && jwt) {
				await initializeAgent();
			}
		};
		initializeAgentHandler();
	}, [dataspaceid, jwt, dataSpaceDataReady]);

	const submitMessageHandler = async () => {
		let placeholderId = null;
		try {
			if (!postContent.trim()) return;

			// Ensure agent is ready before sending message
			if (coachStatus === 'initializing' || !assistant_id || !thread_id) {
				snackbarAlertContext.setSnackbarAlert({
					severity: 'error',
					msg: 'SportWise Coach is still initializing. Please try again in a few seconds.',
				});
				return;
			}

			if (coachStatus === 'failed') {
				snackbarAlertContext.setSnackbarAlert({
					severity: 'error',
					msg: COACH_INIT_ERROR,
				});
				return;
			}

			trackEvent({
				userDetails: { userId: userProfile._id, email: userProfile.email },
				eventDetails: {
					types: ['KissMetrics', 'Segment', 'Encharge', 'AppInsights'],
					eventName: 'WebApp_User sent message to chatbot.',
				},
			});

			// Store message content before clearing it
			const messageContent = postContent;
			console.log('➡️ Message submitted:', messageContent);

			// 👤 Show user message
			const userMessageId = Date.now();
			setConversation((prev) => [...prev, { id: userMessageId, role: 'human', content: messageContent }]);
			setPostContent('');

			// ⏳ Show spinner
			placeholderId = userMessageId + 1;
			setConversation((prev) => [...prev, { id: placeholderId, role: 'bot', content: <Spinner /> }]);

			// 📨 Run message with reused thread
			console.log('📤 Sending to LangGraph → assistant:', assistant_id, 'thread:', thread_id);

			// Send message to LangGraph
			const newresponse = await chatAgentProxyFetch({
				method: 'POST',
				jwt,
				body: {
					path: `threads/${thread_id}/runs/stream`,
					assistant_id: assistant_id,
					thread_id: thread_id,
					config: {
						configurable: {
							DataSpaceId: dataspaceid,
							jwt: jwt,
						},
					},
					input: {
						messages: [
							{
								role: 'human',
								content: messageContent,
							},
						],
					},
					stream_mode: 'updates',
				},
			});

			if (!newresponse.ok) {
				const detail = await parseErrorBody(newresponse);
				throw new Error(detail || `Coach request failed (${newresponse.status})`);
			}

			// 🧠 Stream response
			if (!newresponse.body) {
				throw new Error('Coach returned an empty response body.');
			}

			const reader = newresponse.body.getReader();
			const decoder = new TextDecoder();
			let fullMessage = '';
			let buffer = '';
			let streamError = null;

			// Helper to extract message content from LangGraph updates
			const processUpdate = (jsonPart) => {
				// 1. Legacy/Simple format check
				if (jsonPart?.chatbot?.messages?.[0]?.content) {
					fullMessage = jsonPart.chatbot.messages[0].content;
					return;
				}

				// 2. Iterate over graph node updates (e.g. 'agent', 'tools', 'ensureDataLoaded')
				Object.keys(jsonPart).forEach((key) => {
					const node = jsonPart[key];
					// We look for a 'messages' array in the node state update
					if (node && Array.isArray(node.messages)) {
						node.messages.forEach((msg) => {
							// Ignore human messages (echoes)
							if (msg.type === 'human' || msg.role === 'human') return;

							// Check for tool calls (sometimes hidden in additional_kwargs)
							const toolCalls = msg.tool_calls || msg.additional_kwargs?.tool_calls;

							// Prefer actual text content
							if (msg.content && typeof msg.content === 'string' && msg.content.trim()) {
								fullMessage = msg.content;
							}
							// If we have tool calls but no text yet, show a status
							else if (toolCalls && toolCalls.length > 0 && !fullMessage) {
								fullMessage = 'Thinking...';
							}
						});
					}
				});
			};

			// Helper to parse and process a single SSE event block
			const processEventBlock = (part) => {
				if (!part.trim()) return;

				if (part.includes('heartbeat')) {
					return;
				}

				const err = extractStreamError(part);
				if (err) {
					streamError = err;
					return;
				}

				// Parse lines to find "data: " prefix and accumulate them
				const lines = part.split(/\r?\n/);
				let eventData = '';

				for (const line of lines) {
					if (line.startsWith('data: ')) {
						eventData += line.substring(6);
					}
				}

				if (!eventData.trim()) return;

				try {
					const jsonPart = JSON.parse(eventData);
					processUpdate(jsonPart);

					// Update conversation in real-time if we have a message
					if (fullMessage) {
						setConversation((prevConversation) =>
							prevConversation.map((m) => (m.id === placeholderId ? { ...m, content: fullMessage } : m)),
						);
					}
				} catch (e) {
					console.error('⚠️ Error parsing SSE data block:', e);
				}
			};

			while (true) {
				const { done, value } = await reader.read();
				if (done) break;

				// Append new chunk to buffer
				buffer += decoder.decode(value, { stream: true });

				// Split messages by double newline (SSE standard delimiter), handling various newline formats
				const parts = buffer.split(/\r?\n\r?\n/);

				// Keep the last part in the buffer as it might be incomplete
				buffer = parts.pop() || '';

				for (const part of parts) {
					processEventBlock(part);
				}
			}

			// Process any remaining buffer content after stream ends
			if (buffer.trim()) {
				// Handle case where multiple events might remain in the final buffer
				const finalParts = buffer.split(/\r?\n\r?\n/);
				for (const part of finalParts) {
					processEventBlock(part);
				}
			}

			if (streamError) {
				throw new Error(streamError);
			}

			const finalContent =
				fullMessage && fullMessage !== 'Thinking...'
					? fullMessage
					: COACH_RESPONSE_ERROR;

			// Final update to ensure state is consistent
			setConversation((prevConversation) =>
				prevConversation.map((m) => (m.id === placeholderId ? { ...m, content: finalContent } : m)),
			);
		} catch (error) {
			console.error('❌ Error sending message to chatbot:', error);
			if (placeholderId) {
				setConversation((prevConversation) =>
					prevConversation.map((m) =>
						m.id === placeholderId
							? {
									...m,
									content: error?.message
										? `${COACH_RESPONSE_ERROR} ${error.message}`
										: COACH_RESPONSE_ERROR,
								}
							: m,
					),
				);
			}
			snackbarAlertContext.setSnackbarAlert({
				severity: 'error',
				msg: error?.message
					? `Error sending message to chatbot: ${error.message}`
					: 'Error sending message to chatbot. Please try again later.',
			});
		}
	};

	const coachStatusBanner =
		coachStatus === 'initializing'
			? 'SportWise Coach is initializing…'
			: coachStatus === 'failed'
				? COACH_INIT_ERROR
				: null;

	return (
		<div className={`modal-dialog${showModal ? ' show' : ''}`} role="dialog" aria-modal="true">
			<div className="modal-header">
				{/* Close Button for Sidebar */}
				<button
					className="close-button"
					aria-label="Close sidebar"
					onClick={closeChatbotClickHandler}
					tabIndex={0}
					type="button"
				>
					<CloseIcon fontSize="medium" />
				</button>
				<h3>SportWise Agent</h3>
			</div>
			<div className="modal-content">
				{coachStatusBanner && (
					<div
						className={`coach-status-banner ${coachStatus === 'failed' ? 'error' : 'info'}`}
						role="status"
					>
						{coachStatusBanner}
						{coachStatus === 'failed' && (
							<button type="button" className="coach-retry-button" onClick={initializeAgent}>
								Retry
							</button>
						)}
					</div>
				)}
				<div className="chat-body" role="log" aria-live="polite" tabIndex={0}>
					{/*
          Render the chat conversation history as message bubbles.
          User messages are aligned right with 👤, agent messages left with 🤖.
          Uses ReactMarkdown for rich formatting in agent responses.
        */}
					{conversation.map((message, index) => {
						const isUser = message.role === 'human';
						const avatar = isUser ? (
							<div className="avatar user-avatar" aria-label="You">
								👤
							</div>
						) : (
							<div className="avatar agent-avatar" aria-label="AI">
								🤖
							</div>
						);
						return (
							<div
								key={index}
								className={`chat-message ${isUser ? 'user' : 'agent'}`}
								tabIndex={0}
								aria-label={isUser ? 'Your message' : 'Agent message'}
							>
								{avatar}
								<div className="bubble">
									{typeof message.content === 'string' ? (
										<ReactMarkdown>{message.content}</ReactMarkdown>
									) : (
										message.content
									)}
								</div>
							</div>
						);
					})}
					<div ref={messagesEndRef} />
				</div>
			</div>
			<ChatInput
				postContent={postContent}
				setPostContent={setPostContent}
				handleRecordClick={handleRecordClick}
				handleCancelRecording={handleCancelRecording}
				isRecording={isRecording}
				isTranscribing={isTranscribing}
				submitMessageHandler={submitMessageHandler}
				audioAnalyser={audioAnalyser}
				disabled={coachStatus !== 'ready'}
			/>
		</div>
	);
});
export default RightSidebar;
